/** @jsx jsx */
import React, { memo, type ReactNode } from 'react';
import { css, styled, jsx } from '@compiled/react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import {
	DragDropContext,
	Draggable,
	Droppable,
	type DraggableProvidedDragHandleProps,
} from 'react-beautiful-dnd';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { GROUP_ELEMENT_ID_PREFIX } from '../../common/constants';
import type { Group } from '../../common/types';
import useCreateGroup from '../../controllers/create-group/index.tsx';
import useGroupFeatures from '../../controllers/group-features/index.tsx';
import useGroupList from '../../controllers/groups-resource/index.tsx';
import useRemoveGroup from '../../controllers/remove-group/index.tsx';
import useReorderGroups from '../../controllers/reorder-groups/index.tsx';
import useUpdateGroup from '../../controllers/update-group/index.tsx';
import CountLabel from './count-label';
import CreateToolbar from './create-toolbar';
import GroupHeader, { removeButtonSelector } from './group-header';
import RemoveGroupModal from './remove-group-modal';
import RequestForm from './request-form';
import TicketTypeList from './ticket-type-list';

type Props = {
	group: Group;
	dragHandleProps?: DraggableProvidedDragHandleProps | null;
};

const PortalGroup = memo<Props>(({ group, dragHandleProps }: Props) => {
	const { id, name } = group;
	const { isOpen, hasOpen, canOpen, hasReorder, hasRemove, hasCreateToolbar, toggleOpen } =
		useGroupFeatures(id);
	const [removeConfirmation, isRemoving, handleRemove] = useRemoveGroup(id);
	const handleUpdate = useUpdateGroup(id);
	const [isValid, isSaving, createError, create] = useCreateGroup(group);

	return (
		<GroupWrapper
			id={`${GROUP_ELEMENT_ID_PREFIX}${id}`}
			data-test-id="servicedesk-portal-settings-request-groups.ui.portal-group"
			isSaving={isSaving}
			isRemoving={isRemoving}
		>
			<>
				<GroupHeader
					groupId={id}
					name={name}
					isOpen={isOpen}
					hasOpen={hasOpen}
					canOpen={canOpen}
					hasReorder={hasReorder}
					hasRemove={hasRemove}
					onOpen={toggleOpen}
					onRemove={handleRemove}
					onUpdate={handleUpdate}
					dragHandleProps={dragHandleProps}
				/>
				{!isOpen ? (
					<LabelWrapper>
						<CountLabel groupId={id} />
					</LabelWrapper>
				) : (
					<>
						<TicketTypeList groupId={id} onRemoveGroup={handleRemove} />
						<RequestForm groupId={id} />
					</>
				)}
				{hasCreateToolbar && (
					<CreateToolbar
						canSave={isValid}
						error={createError}
						isSaving={isSaving}
						onCreate={create}
						onCancel={handleRemove}
					/>
				)}
				{removeConfirmation && <RemoveGroupModal {...removeConfirmation} />}
			</>
		</GroupWrapper>
	);
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DraggableGroups = memo<Record<any, any>>(() => (
	<>
		{useGroupList().map<ReactNode>((group: Group, index: number) => (
			<Draggable key={group.id} draggableId={`draggable-group-${group.id}`} index={index}>
				{({ draggableProps, dragHandleProps, innerRef }, { isDragging }) => (
					<DragWrapper {...draggableProps} ref={innerRef} isDragging={isDragging}>
						<PortalGroup group={group} dragHandleProps={dragHandleProps} />
					</DragWrapper>
				)}
			</Draggable>
		))}
	</>
));

export const GroupList = () => {
	const reorderGroups = useReorderGroups();
	return (
		<DragDropContext onDragEnd={reorderGroups}>
			<Droppable droppableId="droppable-group">
				{({ droppableProps, innerRef, placeholder }, { isDraggingOver }) => (
					<GroupListWrapper {...droppableProps} ref={innerRef} isDraggingOver={isDraggingOver}>
						<DraggableGroups />
						{placeholder}
					</GroupListWrapper>
				)}
			</Droppable>
		</DragDropContext>
	);
};

export default PortalGroup;

type GroupWrapperStylesProps = {
	isSaving: boolean;
	isRemoving: boolean;
};

const groupWrapperSelectorValue =
	'servicedesk-portal-settings-request-groups.ui.portal-group.group-wrapper';
const groupWrapperSelector = `[data-component-selector="${groupWrapperSelectorValue}"]`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const GroupWrapper = (props: any) => (
	<div
		{...props}
		data-component-selector={groupWrapperSelector}
		css={[
			groupWrapperStyles,
			props.isSaving && groupSavingStyles,
			props.isRemoving && groupRemovingStyles,
		]}
	/>
);

const groupWrapperStyles = css<GroupWrapperStylesProps>({
	borderRadius: '3px',
	paddingTop: token('space.100', '8px'),
	paddingBottom: token('space.150', '12px'),
	boxShadow: token(
		'elevation.shadow.raised',
		'0 1px 1px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
	),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.raised', colors.N0),
	transition: `
            max-height 0.5s,
            opacity 0.5s,
            margin-top 0.3s 0.3s ease-out`,
	maxHeight: '600px',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`&:hover ${removeButtonSelector}`]: {
		opacity: 1,
	},
});

const groupSavingStyles = css({
	pointerEvents: 'none',
	opacity: 0.5,
});

const groupRemovingStyles = css({
	pointerEvents: 'none',
	overflowY: 'hidden',
	maxHeight: 0,
	opacity: 0,
	/* Negate padding and margin so there is no gap between other groups */
	marginTop: token('space.negative.250', '-20px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DragWrapper = styled.div<{
	isDragging: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ref: (element: HTMLElement | null) => any;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ isDragging }) =>
		isDragging &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css({
			boxShadow: token(
				'elevation.shadow.overlay',
				'0px 8px 16px rgba(9, 30, 66, 0.25), 0px 0px 1px rgba(9, 30, 66, 0.31)',
			),
			borderRadius: '3px',

			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			[groupWrapperSelector]: {
				boxShadow: 'none',
			},
		}),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GroupListWrapper = styled.div<{
	isDraggingOver: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ref: (element: HTMLElement | null) => any;
}>({
	marginBottom: token('space.300', '24px'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ isDraggingOver }) =>
		isDraggingOver &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			[groupWrapperSelector]: {
				pointerEvents: 'none',
			},
		}),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'> :not(:first-of-type)': {
		marginTop: token('space.100', '8px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LabelWrapper = styled.div({
	/* For exact alignment */
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '34px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginTop: '-1px',
});
